import React from "react"

import accessTokenStore from "@store/auth/access-token.store"

import { UNSUPPORTED_LABEL, UNSUPPORTED_MARK } from "./copilotCitations"
import { findMarkdownLinks } from "./copilotSourceQuality"
import {
  isTableSeparatorRow,
  normalizeMarkdownTables,
  splitTableCells,
  tableAlignments,
} from "./copilotTables"

import styles from "./CopilotStudioSdkChat.module.sass"

const REFERENCE_LINE = /^\[\d+\]:\s+\S.*$/
const BULLET = /^[-*]\s+(.*)$/
const NUMBERED = /^\d+\.\s+(.*)$/
const HEADING = /^#{1,3}\s+(.*)$/
const RULE = /^[-*_]{3,}$/
const INLINE = new RegExp(
  `\\*\\*([^*]+)\\*\\*|\\*([^*\\n]+)\\*|\`([^\`]+)\`|\\[(\\d+)\\]|(${UNSUPPORTED_MARK})`,
  "g"
)
const IMAGE_LINE = /^!\[((?:[^\]\\]|\\.)*)\]\((https?:\/\/[^\s)]+)\)\s*(.*)$/

/** LAB-113: how a [n] marker opens its source. Without it, markers stay plain superscripts. */
export type CitationMarkerHandler = {
  open: (position: number) => void
  label: (position: number) => string | undefined
}

const CitationMarkerContext = React.createContext<CitationMarkerHandler | null>(null)

const CitationMarker: React.FC<{ position: number }> = ({ position }) => {
  const handler = React.useContext(CitationMarkerContext)
  const label = handler?.label(position)
  if (!handler || !label) return <sup>{position}</sup>
  return (
    <button
      type="button"
      className={styles.citationMarker}
      onClick={() => handler.open(position)}
      aria-label={`Source ${position}: ${label}`}
      title={label}
    >
      {position}
    </button>
  )
}

function presentCitationMarkers(text: string) {
  return text
    .replace(/\u3010cite:([^】]+)\u3011/gi, "$1")
    .replace(/\[cite:([^\]]+)\]/gi, "$1")
    .replace(/\u3010\d+:[^\u3011]*\u3011/g, "")
    .replace(/cite:(\S+?\.(?:pdf|docx?|pptx?|xlsx?))/gi, "$1")
}

function isTableRow(line: string) {
  const trimmed = line.trim()
  return trimmed.startsWith("|") && trimmed.endsWith("|") && trimmed.split("|").length > 2
}

function renderInline(text: string, keyBase: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  const pattern = new RegExp(INLINE.source, "g")
  let last = 0
  let index = 0
  let match = pattern.exec(text)
  while (match) {
    if (match.index > last) nodes.push(text.slice(last, match.index))
    const key = `${keyBase}-${index}`
    index += 1
    if (match[1] != null) {
      nodes.push(<strong key={key}>{renderInline(match[1], key)}</strong>)
    } else if (match[2] != null) {
      nodes.push(<em key={key}>{match[2]}</em>)
    } else if (match[3] != null) {
      nodes.push(<code key={key}>{match[3]}</code>)
    } else if (match[4] != null) {
      nodes.push(<CitationMarker key={key} position={Number(match[4])} />)
    } else if (match[5] != null) {
      nodes.push(
        <span key={key} className={styles.unsupportedClaim}>
          {UNSUPPORTED_LABEL}
        </span>
      )
    }
    last = match.index + match[0].length
    match = pattern.exec(text)
  }
  if (last < text.length) nodes.push(text.slice(last))
  return nodes
}

function renderWithLinks(text: string, keyBase: string): React.ReactNode[] {
  const links = findMarkdownLinks(text)
  if (!links.length) return renderInline(text, keyBase)
  const nodes: React.ReactNode[] = []
  let last = 0
  links.forEach((link, index) => {
    // An image inside a sentence stays a link: pictures render only on lines of their own.
    const isImage = link.start > last && text[link.start - 1] === "!"
    const before = text.slice(last, isImage ? link.start - 1 : link.start)
    if (before) {
      nodes.push(...renderInline(before, `${keyBase}-t${index}`))
    }
    const key = `${keyBase}-a${index}`
    nodes.push(
      <a key={key} href={link.url} target="_blank" rel="noreferrer">
        {isImage ? `Image: ${link.title}` : renderInline(link.title, key)}
      </a>
    )
    last = link.end
  })
  if (last < text.length) nodes.push(...renderInline(text.slice(last), `${keyBase}-tail`))
  return nodes
}

/** Table cells keep their line breaks: agents and card cells use `<br>` for them. */
function renderCell(text: string, keyBase: string): React.ReactNode[] {
  const parts = text.split(/<br\s*\/?>/i)
  return parts.flatMap((part, index) => {
    const nodes = renderWithLinks(part.trim(), `${keyBase}-${index}`)
    return index < parts.length - 1
      ? [...nodes, <br key={`${keyBase}-br-${index}`} />]
      : nodes
  })
}

function imageHost(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "")
  } catch {
    return "the source"
  }
}

type ImageItem = { alt: string; url: string; caption: string }

function isSharePointImageProxy(url: string) {
  return /copilot-sharepoint-image/i.test(url || "")
}

function sourcePageUrl(item: ImageItem) {
  const fromCaption = findMarkdownLinks(item.caption || "").find((link) =>
    /^https:\/\//i.test(link.url)
  )
  return fromCaption?.url || item.url
}

/**
 * LAB-109 / LAB-102: product picture with its source. Public https images load
 * directly; SharePoint files go through the authenticated LabWizard proxy.
 */
const CopilotImage: React.FC<{ item: ImageItem; keyBase: string }> = ({
  item,
  keyBase,
}) => {
  const [failed, setFailed] = React.useState(false)
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null)
  const alt = item.alt.replace(/\\(.)/g, "$1").trim() || "Product image"
  const sharePointProxy = isSharePointImageProxy(item.url)
  const openUrl = sourcePageUrl(item)

  React.useEffect(() => {
    if (!sharePointProxy) return undefined
    let revoked = false
    let objectUrl = ""
    const token = accessTokenStore.accessToken
    if (!token) {
      setFailed(true)
      return undefined
    }
    ;(async () => {
      try {
        const response = await fetch(item.url, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!response.ok) throw new Error(`image ${response.status}`)
        const blob = await response.blob()
        if (revoked) return
        objectUrl = URL.createObjectURL(blob)
        setBlobUrl(objectUrl)
      } catch {
        if (!revoked) setFailed(true)
      }
    })()
    return () => {
      revoked = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [item.url, sharePointProxy])

  const displaySrc = sharePointProxy ? blobUrl : item.url
  const loadable =
    Boolean(displaySrc) &&
    (sharePointProxy || /^https:\/\//i.test(item.url)) &&
    !failed

  return (
    <figure className={styles.imageFigure}>
      {loadable && displaySrc ? (
        <a href={openUrl} target="_blank" rel="noreferrer">
          <img
            src={displaySrc}
            alt={alt}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setFailed(true)}
          />
        </a>
      ) : (
        <a className={styles.imageFallback} href={openUrl} target="_blank" rel="noreferrer">
          {failed ? `Image could not be loaded. Open "${alt}"` : `Open image "${alt}"`}
        </a>
      )}
      <figcaption>
        {item.caption
          ? renderWithLinks(item.caption, `${keyBase}-cap`)
          : [
              "Image from ",
              <a key={`${keyBase}-host`} href={openUrl} target="_blank" rel="noreferrer">
                {imageHost(openUrl)}
              </a>,
            ]}
      </figcaption>
    </figure>
  )
}

type ListBlock = { type: "ul" | "ol"; items: string[] }

const CopilotMessageBody: React.FC<{ text: string; citations?: CitationMarkerHandler }> = ({
  text,
  citations,
}) => {
  // LAB-106: malformed tables (no outer pipes, squashed onto one line, fenced) are
  // rewritten as well-formed markdown tables before rendering.
  const normalized = normalizeMarkdownTables(presentCitationMarkers(text))
    .replace(/\r\n/g, "\n")
    .replace(/(\S)\*\*(?=[A-Za-z0-9])/g, "$1 **")
  const lines = normalized
    .split("\n")
    .filter((line) => !REFERENCE_LINE.test(line.trim()))

  const blocks: React.ReactNode[] = []
  let paragraph: string[] = []
  let list: ListBlock | null = null
  let tableRows: string[] | null = null
  let gallery: ImageItem[] | null = null
  let count = 0

  const flushGallery = () => {
    if (!gallery) return
    const items = gallery
    gallery = null
    const key = `g-${count}`
    count += 1
    blocks.push(
      <div key={key} className={styles.imageGallery}>
        {items.map((item, index) => (
          <CopilotImage
            // eslint-disable-next-line react/no-array-index-key
            key={`${key}-${index}`}
            item={item}
            keyBase={`${key}-${index}`}
          />
        ))}
      </div>
    )
  }

  const flushParagraph = () => {
    if (!paragraph.length) return
    const content = paragraph.join(" ").trim()
    paragraph = []
    if (!content) return
    const key = `p-${count}`
    count += 1
    blocks.push(<p key={key}>{renderWithLinks(content, key)}</p>)
  }

  const flushTable = () => {
    if (!tableRows) return
    const rows = tableRows
    tableRows = null
    const separator = rows.find(isTableSeparatorRow)
    const dataRows = rows.filter((row) => !isTableSeparatorRow(row))
    if (dataRows.length < 2) {
      dataRows.forEach((row) => paragraph.push(row))
      return
    }
    const header = splitTableCells(dataRows[0])
    const body = dataRows.slice(1).map(splitTableCells)
    const align = separator ? tableAlignments(separator) : []
    const key = `t-${count}`
    count += 1
    // The first column names the attribute being compared, so it is a row header
    // that stays in view while wide comparisons scroll sideways.
    blocks.push(
      <div
        key={key}
        className={styles.tableScroll}
        role="region"
        aria-label="Table"
        // A scrollable region must be focusable so keyboard users can scroll it.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
      >
        <table
          className={header.length > 2 ? styles.comparisonTable : undefined}
        >
          <thead>
            <tr>
              {header.map((cell, cellIndex) => (
                <th
                  // Table cells are positional; stable ids are not available from markdown.
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${key}-h-${cellIndex}`}
                  scope="col"
                  style={
                    align[cellIndex]
                      ? { textAlign: align[cellIndex] }
                      : undefined
                  }
                >
                  {renderCell(cell, `${key}-h-${cellIndex}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, rowIndex) => (
              <tr
                // eslint-disable-next-line react/no-array-index-key
                key={`${key}-r-${rowIndex}`}
              >
                {header.map((_, cellIndex) => {
                  const cellKey = `${key}-c-${rowIndex}-${cellIndex}`
                  const style = align[cellIndex]
                    ? { textAlign: align[cellIndex] }
                    : undefined
                  const content = renderCell(row[cellIndex] || "", cellKey)
                  return cellIndex === 0 ? (
                    <th key={cellKey} scope="row" style={style}>
                      {content}
                    </th>
                  ) : (
                    <td key={cellKey} style={style}>
                      {content}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const flushList = () => {
    if (!list) return
    const current = list
    list = null
    const key = `l-${count}`
    count += 1
    const ListTag = current.type
    blocks.push(
      <ListTag key={key}>
        {current.items.map((item) => {
          const itemKey = `${key}-${count}`
          count += 1
          return (
            <li key={itemKey}>{renderWithLinks(item, itemKey)}</li>
          )
        })}
      </ListTag>
    )
  }

  lines.forEach((raw) => {
    const line = raw.trim()
    const image = line.match(IMAGE_LINE)
    if (image) {
      flushParagraph()
      flushList()
      flushTable()
      if (!gallery) gallery = []
      gallery.push({ alt: image[1], url: image[2], caption: image[3].trim() })
      return
    }
    // Card images arrive separated by blank lines; they still belong in one gallery.
    if (line) flushGallery()
    if (!line || RULE.test(line)) {
      flushList()
      flushTable()
      flushParagraph()
      return
    }
    if (isTableRow(line)) {
      flushParagraph()
      flushList()
      if (!tableRows) tableRows = []
      tableRows.push(line)
      return
    }
    flushTable()
    const heading = line.match(HEADING)
    const bullet = line.match(BULLET)
    const numbered = line.match(NUMBERED)
    if (heading) {
      flushParagraph()
      flushList()
      const key = `h-${count}`
      count += 1
      blocks.push(
        <p key={key}>
          <strong>{renderWithLinks(heading[1], key)}</strong>
        </p>
      )
      return
    }
    if (bullet) {
      flushParagraph()
      if (!list || list.type !== "ul") {
        flushList()
        list = { type: "ul", items: [] }
      }
      list.items.push(bullet[1])
      return
    }
    if (numbered) {
      flushParagraph()
      if (!list || list.type !== "ol") {
        flushList()
        list = { type: "ol", items: [] }
      }
      list.items.push(numbered[1])
      return
    }
    flushList()
    paragraph.push(line)
  })
  flushGallery()
  flushList()
  flushTable()
  flushParagraph()

  return (
    <CitationMarkerContext.Provider value={citations || null}>
      <div className="copilot-message-body">{blocks}</div>
    </CitationMarkerContext.Provider>
  )
}

export default CopilotMessageBody
